import React from 'react';
import {
  Box,
  Container,
  VStack,
  HStack,
  Text,
  Heading,
  Image,
  Button,
  useBreakpointValue,
} from '@chakra-ui/react';

const StarRating = ({ value = 5 }) => {
  const stars = Array.from({ length: 5 }).map((_, i) => (
    <Text key={i} as="span" fontSize="xl" color={i < value ? '#fbbf24' : 'gray.300'}>★</Text>
  ));
  return <HStack spacing={0.5}>{stars}</HStack>;
};

const ReviewCard = ({ review }) => {
  return (
    <Box
      position="relative"
      bg="rgba(255,255,255,0.85)"
      borderRadius="24px"
      p={{ base: 5, md: 6 }}
      border="1px solid"
      borderColor="whiteAlpha.600"
      boxShadow="0 20px 60px rgba(0,0,0,0.08)"
      backdropFilter="blur(8px)"
      transition="all 0.3s ease"
      _hover={{ transform: 'translateY(-4px)', boxShadow: '0 30px 70px rgba(0,0,0,0.10)' }}
      h="100%"
    >
      <Box position="absolute" top={0} left={0} right={0} h="4px" bg="linear-gradient(90deg, #094c26, #0ea5e9, #8b5cf6)" borderTopLeftRadius="24px" borderTopRightRadius="24px" />
      <VStack align="start" spacing={4}>
        <HStack spacing={3} align="center" w="100%">
          <VStack align="start" spacing={0}>
            <Text fontWeight={800} color="gray.800">{review.author}</Text>
            <Text fontSize="xs" color="gray.500">{review.date || ''}</Text>
          </VStack>
          <Box flex={1} />
          <StarRating value={review.rating || 5} />
        </HStack>

        <HStack align="start" spacing={3} w="100%">
          <Text as="span" fontSize="2xl" color="#094c26" fontWeight={800} lineHeight={1}>“</Text>
          <Text color="gray.700" fontSize="md" lineHeight="1.6">{review.text}</Text>
        </HStack>

        {(review.images && review.images.length > 0) && (
          <HStack spacing={3} pt={1}>
            {review.images.slice(0, 2).map((src, idx) => (
              <Image key={idx} src={src} alt={`photo-${idx}`} borderRadius="16px" w="50%" objectFit="cover" />
            ))}
          </HStack>
        )}
      </VStack>
    </Box>
  );
};

const Reviews = ({ reviews: reviewsProp }) => {
  const visibleCount = useBreakpointValue({ base: 1, md: 2, lg: 3 });

  // Données issues des captures envoyées
  const reviews = reviewsProp || [
    {
      author: 'Alicia Sayas',
      rating: 5,
      date: 'Il y a 9 semaines',
      text:
        "Très satisfaite du professionnalisme de M. Hallouche. Très consciencieux, il explique très bien les démarches et rassurant. Aussi et fondamental il est très disponible. Concernant l'entreprise top de la gestion de la DP à la pose. Maintenant il faudra attendre le raccordement par enedis et voir la performance des panneaux solaires. 1ere conclusion très positive.",
      images: [],
    },
    {
      author: 'Sadika TAARKOUBT',
      rating: 5,
      date: 'Il y a 6 semaines',
      text:
        "Monsieur Mariani a su répondre à toutes nos questions et nous a proposé une solution pour l’installation d’une pompe à chaleur air air et des panneaux photovoltaïques. L’intervention a été rapidement programmée et réalisée par une équipe très professionnelle, qui a effectué une installation soignée et laissé le chantier parfaitement propre à la fin. Je recommande vivement cette entreprise pour toute installation dans ce domaine.",
      images: [],
    },
    {
      author: 'Fanou Tartas',
      rating: 5,
      date: 'Il y a 5 semaines',
      text:
        'Les travaux ce sont très bien passé. Le suivi de chantier et le sens du service sont remarquables.',
      images: [],
    },
    {
      author: 'Jean CHANU',
      rating: 5,
      date: 'Il y a 2 jours',
      text: 'Installation parfaite. Professionnalisme. Très bon accompagnement',
      images: [],
    },
    {
      author: 'Sandrine Dumont',
      rating: 5,
      date: 'Il y a 39 semaines',
      text: 'Très belle expérience, rapide et de très bons conseils et accompagnement de monsieur haalouche qui a compris mes besoins. Tout a fonctionné du premier coup, un vrai bonheur. Les installateurs ont été très sympathiques, efficaces et disponibles. Très bons retours',
      images: [],
    },
    {
      author: 'Emeline',
      rating: 5,
      date: 'Il y a 44 semaines',
      text: "Très professionnel, le commercial explique bien et donne toutes les informations, et reste a dispo si besoin même après l'installation. Techniciens très pros, le chantier est propre, rapide, fini en une journée et demie",
      images: [],
    },
    {
      author: 'marina berdji',
      rating: 5,
      date: 'Il y a 42 semaines',
      text: "Mr Kamal DRABI est le conseiller commercial qui nous a vendu une pompe à chaleur, il connaît parfaitement son métier et sait qu’il vend des produits de qualité. Il fait un suivi et un accompagnement clients remarquable. Il est au top! Je suis plus que ravie et je recommande Solarenov",
      images: [],
    },
  ];

  const [index, setIndex] = React.useState(0);
  const count = reviews.length;
  const maxIndex = Math.max(0, count - (visibleCount || 1));
  const prev = () => setIndex((i) => Math.max(0, i - 1));
  const next = () => setIndex((i) => Math.min(maxIndex, i + 1));

  return (
    <Box as="section" py={{ base: 14, lg: 20 }} position="relative">
      <Box
        position="absolute"
        left="50%"
        ml="-50vw"
        w="100vw"
        top={0}
        bottom={0}
        bg="linear-gradient(135deg, #f8fafc 0%, #eef2ff 100%)"
        zIndex={0}
      />
      <Container maxW="1200px" px={5} position="relative" zIndex={1}>
        <VStack spacing={6} textAlign="center" mb={{ base: 10, lg: 14 }}>
          <Box w="80px" h="8px" bg="linear-gradient(90deg, #094c26, #0ea5e9, #8b5cf6)" borderRadius="full" />
          <Heading as="h2" size="xl" fontWeight={900} letterSpacing="-0.02em" color="gray.900">
            Ils nous font confiance
          </Heading>
          <Text color="gray.600" maxW="2xl">
            Avis vérifiés de clients satisfaits, au design inspiré d’iOS.
          </Text>
        </VStack>

        <Box position="relative">
          <Box overflow="hidden">
            <HStack
              spacing={{ base: 4, md: 5 }}
              align="stretch"
              style={{
                width: '100%',
                transform: `translateX(-${index * (100 / (visibleCount || 1))}%)`,
                transition: 'transform 0.35s ease',
              }}
            >
              {reviews.map((review, i) => (
                <Box key={i} flex={`0 0 calc(${100 / (visibleCount || 1)}% - 16px)`}>
                  <ReviewCard review={review} />
                </Box>
              ))}
            </HStack>
          </Box>

          <HStack justify="space-between" mt={5}>
            <Button onClick={prev} variant="outline" borderRadius="full" px={6} borderColor="#094c26" color="#094c26" _hover={{ bg: '#094c26', color: 'white' }}>← Précédent</Button>
            <HStack spacing={2}>
              {Array.from({ length: maxIndex + 1 }).map((_, i) => (
                <Box key={i} w="8px" h="8px" borderRadius="full" bg={i === index ? '#094c26' : 'gray.300'} />
              ))}
            </HStack>
            <Button onClick={next} bg="#094c26" color="white" borderRadius="full" px={6} _hover={{ bg: '#0d6b35' }}>Suivant →</Button>
          </HStack>
        </Box>
      </Container>
    </Box>
  );
};

export default Reviews;


